import React from "react";
import {
  DocumentIcon,
  ArrowUturnLeftIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { PageI } from "../../../types";
import Button from "../../../componentLibrary/Button";
import { pluralize } from "../../../utils/pluralize";

interface DeletedPagesTabContentProps {
  pages: PageI[];
  selectedPageId: string | null;
  onSelectPage: (page: PageI) => void;
  onRestorePage: (pageId: string, title: string) => void;
  isRestoring: boolean;
}

const DeletedPagesTabContent: React.FC<DeletedPagesTabContentProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  onRestorePage,
  isRestoring,
}) => {
  if (pages.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No pages pending deletion
      </div>
    );
  }

  const getDaysRemaining = (deletionRequestedAt: string | null | undefined) => {
    if (!deletionRequestedAt) return 7;
    const deletionDate = new Date(deletionRequestedAt);
    const permanentDeletionDate = new Date(deletionDate);
    permanentDeletionDate.setDate(permanentDeletionDate.getDate() + 7);
    const now = new Date();
    const diffTime = permanentDeletionDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatDeletedDate = (
    deletionRequestedAt: string | null | undefined,
  ) => {
    if (!deletionRequestedAt) return "Unknown";
    return new Date(deletionRequestedAt).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="divide-y">
      {pages.map((page: PageI) => {
        const daysRemaining = getDaysRemaining(page.deletionRequestedAt);
        const isUrgent = daysRemaining <= 2;

        return (
          <div
            key={page._id}
            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
              selectedPageId === page._id
                ? "bg-amber-50 border-l-4 border-amber-500"
                : ""
            }`}
            onClick={() => onSelectPage(page)}
          >
            <div className="flex items-start gap-3">
              <DocumentIcon className="w-8 h-8 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {page.title || "Untitled Page"}
                </p>
                <p className="text-xs text-gray-500">
                  {page.components?.length || 0} components
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <ClockIcon
                    className={`w-3 h-3 ${isUrgent ? "text-red-500" : "text-amber-500"}`}
                  />
                  <p
                    className={`text-xs ${isUrgent ? "text-red-600 font-medium" : "text-amber-600"}`}
                  >
                    {pluralize("day", daysRemaining, true)} remaining
                  </p>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Deleted: {formatDeletedDate(page.deletionRequestedAt)}
                </p>
              </div>
              <Button
                variant="secondary"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestorePage(page._id!, page.title);
                }}
                disabled={isRestoring}
                title="Restore this page"
              >
                <ArrowUturnLeftIcon className="w-3 h-3 mr-1" />
                Restore
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DeletedPagesTabContent;
